import { GetChatDetectedChartingCredentialsResponseSchema } from "@clipboard-health/contract-backend-main";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type QueryClient, type QueryKey, type UseQueryResult } from "@tanstack/react-query";
import type z from "zod";

interface GetChatDetectedChartingCredentialsParams {
  workerId: string;
  workplaceId: string;
  status: "pending" | "saved" | "dismissed";
}

type GetChatDetectedChartingCredentialsResponseSchemaInput = z.input<
  typeof GetChatDetectedChartingCredentialsResponseSchema
>;

export function getChatDetectedChartingCredentialsQueryKey(
  params: GetChatDetectedChartingCredentialsParams
): QueryKey {
  const { workerId, workplaceId, status } = params;
  const url = `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/chat-detected-charting-credentials`;
  const queryParams = {
    filter: {
      "workplace.id": workplaceId,
      state: status,
    },
  };
  return [url, queryParams];
}

export async function invalidateChatDetectedChartingCredentials(
  queryClient: QueryClient,
  params: GetChatDetectedChartingCredentialsParams
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: getChatDetectedChartingCredentialsQueryKey(params),
  });
}

export function useGetChatDetectedChartingCredentials(
  params: GetChatDetectedChartingCredentialsParams,
  options?: UseGetQueryOptions<GetChatDetectedChartingCredentialsResponseSchemaInput>
): UseQueryResult<GetChatDetectedChartingCredentialsResponseSchemaInput> {
  const { workerId, workplaceId, status } = params;

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/chat-detected-charting-credentials`,
    responseSchema: GetChatDetectedChartingCredentialsResponseSchema,
    queryParams: {
      filter: {
        "workplace.id": workplaceId,
        state: status,
      },
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_CHAT_DETECTED_CHARTING_CREDENTIALS_FAILURE,
    },
    ...options,
  });
}
